import React from "react";
import {
  isBrowserLightMode,
  InternalServerErrorDarkIllustration,
  InternalServerErrorLightIllustration,
  RootContainer,
  SEND_EMAIL_LINK,
} from "@pulse/shared-components";
import {
  Button,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { Stack, Typography, useMediaQuery } from "@mui/material";
import { DESKTOP_SCREEN_CONDITION } from "@pulse/shared-components";
import { useTranslation } from "react-i18next";

export interface InternalServerErrorPageProps {
  onBackClick: () => void;
  backButtonLabel: string;
}

const SUPPORT_EMAIL_ID = "it@insightsfeedback.com";

export const InternalServerErrorPage = ({
  onBackClick,
  backButtonLabel,
}: InternalServerErrorPageProps): React.ReactElement => {
  const { t } = useTranslation();
  const isLightMode = isBrowserLightMode();
  const tokens = useFoundationColorTokens();
  const spacing = useSpacing();
  const typography = useTypography();
  const isBigScreen = useMediaQuery(DESKTOP_SCREEN_CONDITION);
  const titleTypography = isBigScreen ? typography.h4 : typography.sh1;
  const bodyTypography = isBigScreen ? typography.b1 : typography.b2;
  const componentGap = isBigScreen ? spacing.space3XL : spacing.spaceXL;
  const textMaxWidth = isBigScreen ? "760px" : "100%";
  const bulletPoints = [
    t(
      "surveyRespondentErrors.surveyRespondentInternalServerErrorBulletPointOne",
    ),
    t(
      "surveyRespondentErrors.surveyRespondentInternalServerErrorBulletPointTwo",
    ),
    t(
      "surveyRespondentErrors.surveyRespondentInternalServerErrorBulletPointThree",
    ),
  ];

  return (
    <RootContainer>
      <Stack
        height="100%"
        alignItems="center"
        justifyContent="center"
        gap={componentGap}
        padding={spacing.spaceLG}
      >
        {isLightMode ? (
          <InternalServerErrorLightIllustration themeTokens={tokens} />
        ) : (
          <InternalServerErrorDarkIllustration themeTokens={tokens} />
        )}
        <Stack
          gap={spacing.spaceSM}
          maxWidth={textMaxWidth}
          alignItems="center"
          textAlign="center"
        >
          <Typography
            sx={{
              ...titleTypography,
              color: tokens.labelHighEmphasis,
              wordBreak: "break-word",
            }}
          >
            {t(
              "surveyRespondentErrors.surveyRespondentInternalServerErrorTitle",
            )}
          </Typography>
          <Stack gap={spacing.spaceXS} alignItems="left" textAlign="left">
            <Typography
              sx={{
                ...bodyTypography,
                color: tokens.label,
              }}
            >
              {t(
                "surveyRespondentErrors.surveyRespondentInternalServerErrorSubtitle",
              )}
            </Typography>
            <Typography
              sx={{
                ...bodyTypography,
                color: tokens.label,
              }}
            >
              {t(
                "surveyRespondentErrors.surveyRespondentInternalServerErrorIntro",
              )}
            </Typography>
            <Stack
              component="ul"
              sx={{
                ...bodyTypography,
                color: tokens.label,
                margin: 0,
                paddingLeft: spacing.spaceLG,
                textAlign: "left",
                width: "100%",
              }}
            >
              {bulletPoints.map((bulletPoint) => (
                <Typography
                  key={bulletPoint}
                  component="li"
                  sx={{
                    ...bodyTypography,
                    color: tokens.label,
                  }}
                >
                  {bulletPoint}
                </Typography>
              ))}
            </Stack>
            <Typography
              sx={{
                ...bodyTypography,
                color: tokens.label,
              }}
            >
              {t(
                "surveyRespondentErrors.surveyRespondentInternalServerErrorSupportText",
              )}{" "}
              <Typography
                component="a"
                href={SEND_EMAIL_LINK}
                target="_blank"
                rel="noreferrer"
                sx={{
                  ...bodyTypography,
                  color: tokens.labelInfo,
                  textDecoration: "none",
                  "&:hover": { cursor: "pointer", textDecoration: "underline" },
                }}
              >
                {SUPPORT_EMAIL_ID}
              </Typography>
            </Typography>
          </Stack>
        </Stack>
        <Button
          size="medium"
          label={backButtonLabel}
          variant="outlined-neutral"
          onClick={onBackClick}
        />
      </Stack>
    </RootContainer>
  );
};
